import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faCheckCircle, faStopCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import useCampaignQuoteTemplates from '@hooks/useCampaignQuoteTemplates'
import { generateUrls } from '@hooks/useCampaignQuoteTemplate'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import CampaignQuoteTemplateListItem from '../CampaignQuoteTemplateListItem'

import type { PageContextPayload } from '../../index'
import DefaultQuoteItems from '../DefaultQuoteItems'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'users',
        entityTitleKey: 'full_name',
        label: 'Assigned User',
      },
      key: 'assigned_user_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'created_after',
      label: 'Created After',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'created_before',
      label: 'Created Before',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'updated_after',
      label: 'Updated After',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'updated_before',
      label: 'Updated Before',
    },
  ]
}

interface PageContentProps {
  agencyId: number,
  pageFilters: {
    created_after?: string,
    created_before?: string,
    updated_after?: string,
    updated_before?: string,
    hidden?: 'true' | 'false',
    string?: string,
    agency_id?: number,
    assigned_user_id?: number,
  },
}

const PageContent = (props: PageContentProps) => {
  const { agencyId: navigationAgencyId, pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { hidden } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden']) as { hidden: 'true' | 'false' }
  const isHidden = hidden || 'false'

  const campaignQuoteTemplatesPayload = useCampaignQuoteTemplates({
    filters: {
      ...pageFilters,
      hidden: isHidden,
      agency_id: navigationAgencyId,
    },
    performHttpRequests: true,
    requestOptions: {},
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignQuoteTemplates,
    filteredCampaignQuoteTemplatesCount,
    hasFilteredCampaignQuoteTemplates,
    loading,
  } = campaignQuoteTemplatesPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateOrEditCampaignQuoteTemplateModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { archivedCampaignQuoteTemplatesUrl, campaignQuoteTemplatesIndexUrl } = generateUrls({}, {
    prefixRoute: `agencies/${navigationAgencyId}/manage`,
  })

  return (
    <>
      <Helmet>
        <title>CampaignQuoteTemplates | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            onClick={() => showCreateOrEditCampaignQuoteTemplateModal()}
            size="medium"
          >
            Create
          </Button>
        )}
        activeTabBarItemKey={isHidden}
        nestedNavigation
        tabBarItems={[
          {
            href: campaignQuoteTemplatesIndexUrl,
            icon: faCheckCircle,
            key: 'false',
            title: 'Current',
          },
          {
            href: archivedCampaignQuoteTemplatesUrl,
            icon: faStopCircle,
            key: 'true',
            title: 'Archived',
          },
        ]}
        title="Quote Templates"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredCampaignQuoteTemplates && <ListBlankState />}

            {!loading && hasFilteredCampaignQuoteTemplates && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Quote Templates">
                <Box flexDirection="column">
                  {filteredCampaignQuoteTemplates.map(campaignQuoteTemplate => (
                    <CampaignQuoteTemplateListItem
                      key={campaignQuoteTemplate.id}
                      campaignQuoteTemplate={campaignQuoteTemplate}
                    />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredCampaignQuoteTemplatesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />

            <DefaultQuoteItems />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
